import type { ProductOffer, ProductMainOffer, Shipping } from 'src/types';
import type { ExtendedProductMainOffer } from 'src/areas/product/components/product-context';
import { getSortedShipping } from './shipping-sort';
import type { FilterOffer, IFilter } from './types';
import { replaceMainOfferWithAlternativeOffer } from './utils';

const filterOffer = (offer: FilterOffer, filter: IFilter): boolean => {
  // Keep offers with missing info if we have a function defined for it
  return filter.missingInfoFn
    ? filter.filterFn(offer, filter.selection) || filter.missingInfoFn(offer)
    : filter.filterFn(offer, filter.selection);
};

function tryFindNewMainOffer(
  offer: ProductMainOffer,
  filter: IFilter
): ProductMainOffer | null {
  const mainOfferIsValid = filter.filterFn(offer, filter.selection);
  if (mainOfferIsValid) return offer;

  const mainOfferHasMissingInfo =
    filter.missingInfoFn && filter.missingInfoFn(offer);

  if (!offer.alternativeOffers?.length) {
    if (mainOfferHasMissingInfo) return offer;
    return null;
  }

  let firstValidAlternativeOffer = offer.alternativeOffers.find(alt =>
    filter.filterFn(alt, filter.selection)
  );

  // If no valid alt offer but a main offer with missing info, return the main offer
  if (!firstValidAlternativeOffer && mainOfferHasMissingInfo) {
    return offer;
  }

  // If no valid alt offer, try finding one with missing info
  if (!firstValidAlternativeOffer && filter.missingInfoFn) {
    firstValidAlternativeOffer = offer.alternativeOffers.find(alt =>
      filter.missingInfoFn!(alt)
    );
  }

  // If no offer (incl. missing info) is valid, return no offer
  if (!firstValidAlternativeOffer) {
    return null;
  }

  return replaceMainOfferWithAlternativeOffer(
    offer,
    firstValidAlternativeOffer
  );
}

export const filterOffersGeneric = (
  offers: ProductMainOffer[],
  filter: IFilter
): ProductMainOffer[] => {
  if (!offers.length || !filter.active) {
    return offers;
  }

  if (
    !filter.filterAlternativeOffers ||
    offers.every(offer => !offer.alternativeOffers?.length)
  ) {
    return offers.filter(mainOffer => filterOffer(mainOffer, filter));
  }

  // Filtering alternative offers
  const filteredOffers: ProductMainOffer[] = [];
  offers.forEach(offer => {
    const filteredAlternatives = offer.alternativeOffers?.filter(alt =>
      filterOffer(alt, filter)
    );
    if (!filteredAlternatives?.length) {
      return;
    }
    const offerWithFilteredAlternatives = {
      ...offer,
      alternativeOffers: filteredAlternatives,
    };
    const newMainOffer = tryFindNewMainOffer(
      offerWithFilteredAlternatives,
      filter
    );
    if (newMainOffer) {
      filteredOffers.push(newMainOffer);
    }
  });
  return filteredOffers;
};

export const filterShippingsGeneric = (
  offers: ProductMainOffer[],
  filters: IFilter[]
): ExtendedProductMainOffer[] => {
  const applicapleFilters = filters.filter(
    filter => filter.active && filter.filterShippingFn
  );

  const filteredOffers = offers.map(
    offer =>
      ({
        ...filterOfferShippings(offer, applicapleFilters),
        unfilteredShippings: offer?.shipping?.nodes,
      } as ExtendedProductMainOffer)
  );

  for (const offer of filteredOffers) {
    if (offer.alternativeOffers) {
      offer.alternativeOffers = offer.alternativeOffers.map(
        altOffer =>
          filterOfferShippings(altOffer!, applicapleFilters) as ProductOffer
      );
    }
  }

  return filteredOffers;
};

const filterOfferShippings = (
  offer: ProductMainOffer | ProductOffer,
  filters: IFilter[]
): ProductMainOffer | ProductOffer => {
  if (!offer.shipping || !offer.shipping.nodes) {
    return offer;
  }

  const shippingNodes = offer.shipping.nodes as Shipping[];

  const filteredShippings = shippingNodes.filter(shipping =>
    filters.every(filter =>
      filter.filterShippingFn!(shipping, filter.selection)
    )
  );
  const cheapest = getSortedShipping(filteredShippings, false);
  const fastest = getSortedShipping(filteredShippings, true);
  return {
    ...offer,
    shipping: { cheapest, fastest, nodes: filteredShippings },
  };
};
